/**
 * The bridge: socket events in, query cache out.
 *
 * Mounted once at the app root. Queries own server state; the socket is only a
 * change-notification channel, so nothing here keeps its own copy of anything.
 *
 * Two ordering defences, both cheap, both in this file:
 *   - `rev` (the conversation's `updated_at` in ms) — a patch that is not newer
 *     than what the cache holds is dropped, so an out-of-order broadcast is a
 *     no-op rather than a rollback.
 *   - `seq` (a per-process counter) — a jump means packets were missed, and the
 *     honest response is to refetch, not to guess what was in them.
 */

import type { InfiniteData, QueryClient } from '@tanstack/react-query';
import { serverEventSchema, type ServerEvent } from '@shared/socket';
import type { ConversationDetail, Message, ParkedRow } from '@shared/types';
import { queueBoardRow } from './boardBuffer';
import { keys } from './keys';
import { setConnection, socket } from './socket';

export interface MessagePage {
  items: Message[];
  nextCursor: string | null;
}
export type MessagesData = InfiniteData<MessagePage, string | null>;

let lastSeq = 0;

/** Reset between tests, and whenever the server process may have restarted. */
export function resetSequence(): void {
  lastSeq = 0;
}

export function startRealtime(client: QueryClient, target: typeof socket = socket): () => void {
  const onEvent = (raw: ServerEvent): void => {
    const parsed = serverEventSchema.safeParse(raw);
    // An event we cannot read is a contract drift, not a reason to crash the
    // board. Resync so the screen is at least true, and let it go.
    if (!parsed.success) {
      fullResync(client);
      return;
    }
    const event = parsed.data;

    if (lastSeq !== 0 && event.seq > lastSeq + 1) fullResync(client);
    lastSeq = Math.max(lastSeq, event.seq);

    switch (event.type) {
      case 'message.created': {
        appendMessage(client, event.conversationId, event.message);
        queueBoardRow(client, event.row);
        break;
      }

      case 'conversation.updated': {
        queueBoardRow(client, event.row);
        patchDetail(client, event.conversationId, event.rev, event.conversation);
        if (event.row.handlingState !== 'parked') removeFromQueue(client, event.conversationId);
        break;
      }

      case 'conversation.parked': {
        queueBoardRow(client, event.row);
        insertIntoQueue(client, event.row);
        patchDetail(client, event.conversationId, event.rev, {
          handlingState: 'parked',
          parkReason: event.reason,
          parkSummary: event.summary,
        });
        break;
      }

      case 'conversation.claimed': {
        // The broadcast the product depends on: this is what makes the row
        // leave every other operator's queue without a refetch.
        removeFromQueue(client, event.conversationId);
        queueBoardRow(client, event.row);
        patchDetail(client, event.conversationId, event.rev, {
          handlingState: 'human',
          assignee: event.by,
        });
        break;
      }

      case 'conversation.handled_back': {
        removeFromQueue(client, event.conversationId);
        queueBoardRow(client, event.row);
        patchDetail(client, event.conversationId, event.rev, {
          handlingState: 'ai',
          assignee: null,
          parkReason: null,
          parkSummary: null,
        });
        break;
      }
    }
  };

  const onConnect = (): void => {
    setConnection('live');
    // Connection State Recovery is documented as best-effort. When it did not
    // recover, the tab has an unknown-sized hole in its history and the only
    // correct move is to refetch.
    if (!target.recovered) fullResync(client);
  };

  const onDisconnect = (): void => {
    setConnection('offline');
  };

  const onVisibility = (): void => {
    if (document.visibilityState !== 'visible') return;
    // iOS suspends both the socket and every timer when the app is
    // backgrounded, and reports neither. Coming back is always a resync.
    if (target.disconnected) target.connect();
    fullResync(client);
  };

  target.on('event', onEvent);
  target.on('connect', onConnect);
  target.on('disconnect', onDisconnect);
  target.io.on('reconnect_attempt', () => {
    setConnection('connecting');
  });
  document.addEventListener('visibilitychange', onVisibility);

  return () => {
    target.off('event', onEvent);
    target.off('connect', onConnect);
    target.off('disconnect', onDisconnect);
    document.removeEventListener('visibilitychange', onVisibility);
  };
}

export function fullResync(client: QueryClient): void {
  lastSeq = 0;
  void client.invalidateQueries({ queryKey: keys.board });
  void client.invalidateQueries({ queryKey: keys.parked });
  void client.invalidateQueries({ queryKey: keys.resolvedToday });
  // Only the threads someone is actually looking at. Invalidating every thread
  // a long shift has visited would refetch history nobody is reading.
  void client.invalidateQueries({ queryKey: keys.conversationRoot, refetchType: 'active' });
}

/* -------------------------------------------------------------------------- */
/* Cache writes                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Append to a thread ONLY if that thread is cached. Without the guard, a shift
 * spent on the board would accumulate a message list for every conversation
 * that has ever spoken, and the tab would grow all day.
 *
 * Page 0 is the newest page and each page is newest-first, which is the order
 * the server pages in.
 */
export function appendMessage(client: QueryClient, conversationId: string, message: Message): void {
  const key = keys.messages(conversationId);
  if (client.getQueryData(key) === undefined) return;

  client.setQueryData<MessagesData>(key, (old) => {
    if (old === undefined) return old;
    const first = old.pages[0];
    if (first === undefined) return old;
    // The same message arrives from the POST response and from the broadcast.
    // Dedupe at the single append site; never append anywhere else.
    if (old.pages.some((page) => page.items.some((m) => m.id === message.id))) return old;

    const pages = [...old.pages];
    pages[0] = { ...first, items: [message, ...first.items] };
    return { ...old, pages };
  });
}

function patchDetail(
  client: QueryClient,
  conversationId: string,
  rev: number,
  changes: Partial<ConversationDetail>,
): void {
  const key = keys.conversation(conversationId);
  if (client.getQueryData(key) === undefined) return;

  client.setQueryData<ConversationDetail>(key, (old) => {
    if (old === undefined) return old;
    if (rev < old.rev) return old;
    return { ...old, ...changes, rev };
  });
}

function removeFromQueue(client: QueryClient, conversationId: string): void {
  client.setQueryData<ParkedRow[]>(keys.parked, (old) => {
    if (old === undefined) return old;
    const next = old.filter((row) => row.conversationId !== conversationId);
    // Referential no-change when it was not there, so nothing re-renders — this
    // is what stops the loser of a claim race seeing the row flash back.
    return next.length === old.length ? old : next;
  });
}

function insertIntoQueue(client: QueryClient, row: ParkedRow): void {
  client.setQueryData<ParkedRow[]>(keys.parked, (old) => {
    if (old === undefined) return old;
    const index = old.findIndex((r) => r.conversationId === row.conversationId);
    if (index === -1) return [...old, row];
    const held = old[index];
    if (held !== undefined && held.rev >= row.rev) return old;
    const next = [...old];
    next[index] = row;
    return next;
  });
}
